/**
 * Client-side service worker registration for the AVOS PWA (Requirement 21.2).
 *
 * Registering an active service worker is what makes a supported browser
 * report install eligibility (together with the linked manifest). This runs in
 * the browser, from the client hydration entry.
 *
 * It is GUARDED on `'serviceWorker' in navigator`: browsers without service
 * worker support (or non-secure contexts where the API is absent) simply skip
 * registration and the app keeps working as plain SSR HTML — the guard means
 * importing/calling this in a non-browser context (e.g. a Node unit test) is a
 * safe no-op rather than a crash.
 */
import { SERVICE_WORKER_PATH } from "./serviceWorkerSource.js";

/** Options for {@link registerServiceWorker}. */
export interface RegisterServiceWorkerOptions {
  /** Path the worker is served at. Defaults to {@link SERVICE_WORKER_PATH}. */
  readonly scriptUrl?: string;
  /** Registration scope. Defaults to the app root `"/"`. */
  readonly scope?: string;
}

/** Outcome of a registration attempt. */
export type RegisterServiceWorkerResult =
  /** The environment has no service worker support; nothing was attempted. */
  | { readonly status: "unsupported" }
  /** Registration succeeded. */
  | { readonly status: "registered"; readonly registration: ServiceWorkerRegistration }
  /** The API was present but registration threw. */
  | { readonly status: "failed"; readonly error: unknown };

/**
 * Register the AVOS service worker if the browser supports it.
 *
 * Fail-closed and non-throwing: when `serviceWorker` is not available on
 * `navigator` it returns `{ status: "unsupported" }` without touching any API;
 * when registration throws it resolves `{ status: "failed" }` rather than
 * rejecting, so a registration failure never breaks hydration.
 */
export async function registerServiceWorker(
  options: RegisterServiceWorkerOptions = {},
): Promise<RegisterServiceWorkerResult> {
  // Guard: no service worker support (older browsers, non-secure contexts, or
  // a non-browser runtime like the test/Node environment).
  if (
    typeof navigator === "undefined" ||
    navigator === null ||
    !("serviceWorker" in navigator)
  ) {
    return { status: "unsupported" };
  }

  const scriptUrl = options.scriptUrl ?? SERVICE_WORKER_PATH;
  const scope = options.scope ?? "/";
  try {
    const registration = await navigator.serviceWorker.register(scriptUrl, {
      scope,
    });
    return { status: "registered", registration };
  } catch (error) {
    return { status: "failed", error };
  }
}
